<template>
  <v-container v-if="faqs.length" class="py-15">
    <h2 class="heading mb-7 font-weight-bold text-center">{{ localizedData.faq.heading }}</h2>
    <v-row class="reveal">
      <v-col cols="12" sm="10" offset-sm="1" md="8" offset-md="2" lg="6" offset-lg="3">
        <ul class="faq">
          <li v-for="(faq, index) in displayedFaqs" :key="index" class="faq-item">
            <div class="faq-question-row">
              <div class="icon icon-q text-body-2 text-sm-body-1" v-html="faq.q"></div>
              <div class="faq-toggle-btn-wrapper">
                <v-btn 
                  class="faq-toggle-btn bg-black" 
                  size="30" 
                  density="comfortable" 
                  variant="text" 
                  @click="toggleAnswer(index)" 
                  :aria-label="expandedIndexes[index] ? '閉じる' : '開く'"
                  style="min-width: 30px; width: 30px; height: 30px; padding: 0; background-color: rgba(0,0,0,0.5) !important; margin: auto 0;">
                  <v-icon 
                    :icon="`mdi-${expandedIndexes[index] ? 'minus' : 'plus'}`" 
                    color="white" 
                    size="20"
                    style="color: white !important;">
                  </v-icon>
                </v-btn>
              </div>
            </div>
            <transition name="faq-slide">
              <div v-if="expandedIndexes[index]" class="icon icon-a text-caption text-sm-body-2 text-pre-wrap faq-answer" v-html="faq.a"></div>
            </transition>
            <hr class="border">
          </li>
          <v-btn rounded outlined @click="toggleShowAllFaqs" style="text-transform:none;">
            {{ showAllFaqs ? localizedData.faq.close : localizedData.faq.more }}
          </v-btn>
        </ul>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import faqQuestions from '@/locales/FaqQuestions.js';

const { locale } = useI18n();

const faqs = ref([]);
const showAllFaqs = ref(false);
const expandedIndexes = ref([]);

const displayedFaqs = computed(() => {
  return showAllFaqs.value ? faqs.value : faqs.value.slice(0, 5);
});

const localizedData = computed(() => {
  console.log('Debug - faqQuestions:', faqQuestions);
  console.log('Debug - locale.value:', locale.value);
  console.log('Debug - faqQuestions[locale.value]:', faqQuestions[locale.value]);
  return faqQuestions[locale.value];
});

const setupFaqs = () => {
  // hiddenプロパティがtrueの質問を除外
  faqs.value = localizedData.value.faq.questions.filter(faq => !faq.hidden);
  expandedIndexes.value = Array(faqs.value.length).fill(false);
};

const toggleShowAllFaqs = () => {
  showAllFaqs.value = !showAllFaqs.value;
  // 一括で開閉状態もリセット
  if (!showAllFaqs.value) {
    expandedIndexes.value = Array(faqs.value.length).fill(false);
  }
};

const toggleAnswer = (index) => {
  expandedIndexes.value[index] = !expandedIndexes.value[index];
};

// JavaScriptによるアニメーション制御を削除

// 初期設定とロケール変更時の更新
watchEffect(() => {
  setupFaqs();
});
</script>

<style lang="scss" scoped>
/* FAQ */
.faq-item {
  margin-bottom: 1.5em;
}
.faq-question-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  width: 100%;
}

.faq-toggle-btn-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  margin-top: auto;
  margin-bottom: auto;
}
.icon.icon-q {
  flex: 1 1 0%;
  min-width: 0;
  word-break: break-word;
  white-space: pre-line;
  display: flex;
  align-items: center;
}
.faq-toggle-btn {
  flex: none;
  min-width: 30px;
  max-width: 30px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 0;
  border-radius: 50%;
  aspect-ratio: 1/1;
  background-color: rgba(0, 0, 0, 0.3);
  
  &:deep(.v-icon) {
    color: white !important;
  }
  
  &:deep(.mdi-plus),
  &:deep(.mdi-minus) {
    color: white !important;
  }
}

.faq-answer {
  padding-left: 50px;
  min-height: 40px;
  display: flex;
  align-items: center;
  margin: 1em 0;
  position: relative;
  overflow: hidden;
}

.faq-answer::after {
  position: absolute;
  content: "";
  display: flex;
  justify-content: center;
  align-items: center;
  left: 0;
  top: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: bold;
}

/* CSSのみのアニメーション - JavaScriptを使わずにスムーズな動きを実現 */
.faq-slide-enter-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 1000px; /* 十分大きな値を設定 */
  opacity: 1;
  transform: translateY(0);
}

.faq-slide-leave-active {
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 1000px;
  opacity: 1;
  transform: translateY(0);
}

.faq-slide-enter-from,
.faq-slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}


.faq {
  list-style-type: none;

  hr.border {
    opacity: 0.3;
    margin-top: 1em;
  }

  .icon {
    position: relative;
    padding-left: 50px;
    min-height: 40px;
    display: flex;
    align-items: center;
    margin: 1em 0;

    &::after {
      position: absolute;
      content: "";
      display: flex;
      justify-content: center;
      align-items: center;
      height: 30px;
      width: 30px;
      border-radius: 60px;
      left: 0;
    }

    &-q::after {
      content: "Q";
      color: #fff;
      background: rgb(var(--v-theme-secondary));
    }

    &-a::after {
      content: "A";
      color: rgb(var(--v-theme-secondary));
      background: #fff;
    }
  }

  > li {
    overflow: hidden;
    transition: max-height 0.8s ease-out; /* アニメーションの追加 */
  }

  > li:last-child {
    border: none;
  }

  .v-btn {
    margin-top: 2em;
  }

  .faq-answer-enter-active,
  .faq-answer-leave-active {
    transition: max-height 0.8s ease-out; /* アニメーションの追加 */
  }

  .faq-answer-enter-to,
  .faq-answer-leave-to {
    max-height: 0;
  }
}
</style>
